const state = {
  narratives: [],
  narrativeIndex: -1,
  interpolateRegex: /\{(.*?)\}/g,
  /** flag to make setNarratives action set the index to `length` instead of `-1` */
  startAtLastNarrative: false,
  /** narratives components can use this to check if the player reached this step by hitting Back and do different logic */
  lastProgressionBackwards: false,
  /** used to cache the lastBackground, for use by carryoverBackground when going beyond the bounds of `narratives` */
  lastBackground: '',
};

const mutations = {
  setLastBackground(state, { background = '' } = {}) { state.lastBackground = background; },
  setLastProgressionBackwards(state, { back = true } = {}) { state.lastProgressionBackwards = back; },
  setStartAtLastNarrative(state, { startAtLastNarrative = true } = {}) { state.startAtLastNarrative = startAtLastNarrative; },
  setNarratives(state, { narratives = [] }) { state.narratives = narratives; },
  setNarrativeIndex(state, { narrativeIndex = -1 }) { state.narrativeIndex = narrativeIndex; }
};

const actions = {
  setNarratives({ commit, getters }, { narratives = [] }) {
    commit('setNarratives', { narratives });
    if (getters.startAtLastNarrative) {
      commit('setNarrativeIndex', { narrativeIndex: narratives.length });
      commit('setStartAtLastNarrative', { startAtLastNarrative: false });
      commit('setLastProgressionBackwards', { back: true });
    } else {
      commit('setNarrativeIndex', { narrativeIndex: -1 });
      commit('setLastProgressionBackwards', { back: false });
    }
  },
  addNarratives({ commit, getters }, { narratives = [] }) {
    commit('setNarratives', { narratives: getters.narratives.concat(narratives) });
  },
  advanceNarrative({ commit, getters: { narrativeIndex = -1, narratives = [] }, dispatch }) {
    commit('setLastProgressionBackwards', { back: false });
    const newNI = narrativeIndex + 1;
    if (newNI >= narratives.length) {
      commit('setNarratives', { narratives: [] });
      commit('setNarrativeIndex', { narrativeIndex: -1 });
      dispatch('setTooltips', { tooltips: [] });
    } else {
      commit('setNarrativeIndex', { narrativeIndex: newNI });
      const narrative = narratives[newNI];
      if (narrative && narrative.tooltips && narrative.tooltips.length) {
        dispatch('setTooltips', { tooltips: narrative.tooltips });
      }
    }
  },
  rewindNarrative({ commit, getters: { narrativeIndex = -1, narratives = [] }, dispatch }) {
    commit('setLastProgressionBackwards', { back: true });
    const newNI = narrativeIndex - 1;
    if (newNI < 0) {
      commit('setNarratives', { narratives: [] });
      commit('setNarrativeIndex', { narrativeIndex: -1 });
      dispatch('setTooltips', { tooltips: [] });
    } else {
      commit('setNarrativeIndex', { narrativeIndex: newNI });
      const narrative = narratives[newNI];
      if (narrative && narrative.tooltips && narrative.tooltips.length) {
        dispatch('setTooltips', { tooltips: narrative.tooltips });
      }
    }
  },
  interpolateText({ getters }, text) {
    return new Promise(resolve =>
      resolve(text.replace(getters.interpolateRegex, (match, tag) => getters.replacements[tag] || match))
    );
  },
};

const getNarrativeBackground = (narrative = {}, playerRole = {}, narratives = [], narrativeIndex = -1, lastBackground = '') => {
  const background =
    narrative.playerBackground
      ? playerRole.background
      : narrative.background;
  if (narrative.carryoverBackground && narratives.length && narrativeIndex >= 0) {
    let ni = narrativeIndex;
    let lastNarrativeBackground = null;
    // console.log(lastBackground);
    // console.log(ni);
    // eslint-disable-next-line no-plusplus
    while (--ni >= 0) {
      // console.log(ni);
      if (!(narratives[ni].carryoverBackground)) {
        lastNarrativeBackground =
          narratives[ni].playerBackground
            ? playerRole.background
            : narratives[ni].background;
        break;
      }
    }
    if (lastNarrativeBackground) {
      return lastNarrativeBackground;
    } else if (lastBackground) {
      return lastBackground;
    }
  }
  return background || '';
};

const getNarrativeIsDialogWithResponse =
  (narrative = {}) => narrative && narrative.type === 'dialog' && narrative.optionalText;

const getNarrativeLeftCharacter =
  (characters, narrative = {}, selectedAvatar, narrator) => {
    const { leftPlayer, leftNarrator, leftCharacter } = narrative;
    const c = leftPlayer ? selectedAvatar
      : (leftNarrator ? narrator : ((leftCharacter != null) ? characters[leftCharacter] : {}));
    return (c != null) ? c : {};
  };

const getNarrativeRightCharacter =
  (characters, narrative = {}, selectedAvatar, narrator) => {
    const { rightPlayer, rightNarrator, rightCharacter } = narrative;
    const c = rightPlayer ? selectedAvatar
      : (rightNarrator ? narrator : ((rightCharacter != null) ? characters[rightCharacter] : {}));
    return (c != null) ? c : {};
  };

const getNarrativeExtraLeftCharacter =
  (characters, narrative = {}, selectedAvatar, narrator) => {
    const { extraLeftPlayer, extraLeftNarrator, extraLeftCharacter } = narrative;
    const c = extraLeftPlayer ? selectedAvatar
      : (extraLeftNarrator ? narrator : ((extraLeftCharacter != null) ? characters[extraLeftCharacter] : {}));
    return (c != null) ? c : {};
  };

const getNarrativeExtraRightCharacter =
  (characters, narrative = {}, selectedAvatar, narrator) => {
    const { extraRightPlayer, extraRightNarrator, extraRightCharacter } = narrative;
    const c = extraRightPlayer ? selectedAvatar
      : (extraRightNarrator ? narrator : ((extraRightCharacter != null) ? characters[extraRightCharacter] : {}));
    return (c != null) ? c : {};
  };

const getters = {
  narratives({ narratives }) { return narratives; },
  narrativeIndex({ narrativeIndex = -1 }) { return narrativeIndex; },
  startAtLastNarrative({ startAtLastNarrative }) { return startAtLastNarrative; },
  lastProgressionBackwards({ lastProgressionBackwards }) { return lastProgressionBackwards; },
  lastBackground({ lastBackground = '' }) { return lastBackground; },
  interpolateRegex({ interpolateRegex }) { return interpolateRegex; },
  replacements(_state, {
    narrativePlayerName,
    narrativeLeftSpeaker,
    narrativeRightSpeaker,
    narrativeCenterSpeaker,
    narrativeCurrentChallenge
  },
  _rootState,
  {
    displayMetricsByReferenceId, // getter from game module
    playerResponsesByReferenceId, // getter from game module
  }) {
    const replacements = {
      player: narrativePlayerName,
      left: narrativeLeftSpeaker,
      right: narrativeRightSpeaker,
      center: narrativeCenterSpeaker,
      challengeTitle: narrativeCurrentChallenge.title,
      challengeDescription: narrativeCurrentChallenge.description
    };
    playerResponsesByReferenceId.forEach((value, key) => {
      replacements[key] = value;
    });
    displayMetricsByReferenceId.forEach((value, key) => {
      const metric = value.metric;
      const currentValue = value.value;
      let replacementValue = currentValue;
      if (metric.displayValues && metric.displayValues.length) {
        metric.displayValues.forEach(({ actualValue, displayValue }) => {
          if (currentValue === actualValue) {
            replacementValue = displayValue;
          }
        });
      }
      replacements[key] = replacementValue;
    });
    return replacements;
  },
  staticNarrativeContent(_, { narrative = {} }) {
    return narrative.type === 'animation' || narrative.type === 'exposition';
  },
  lastNarrative({ narratives = [], narrativeIndex = -1 }) {
    const lastIndex = narrativeIndex - 1;
    return lastIndex < 0 ? undefined : narratives[lastIndex];
  },
  lastNarrativeBackground(
    { narratives = [], narrativeIndex = -1, lastBackground = '' },
    { lastNarrative = {}, playerRole = {} },
  ) {
    return getNarrativeBackground(lastNarrative, playerRole, narratives, narrativeIndex - 1, lastBackground);
  },
  lastNarrativeLeftCharacter(_, { characters, lastNarrative = {}, selectedAvatar, narrator }) {
    return getNarrativeLeftCharacter(characters, lastNarrative, selectedAvatar, narrator);
  },
  lastNarrativeRightCharacter(_, { characters, lastNarrative = {}, selectedAvatar, narrator }) {
    return getNarrativeRightCharacter(characters, lastNarrative, selectedAvatar, narrator);
  },
  lastNarrativeExtraLeftCharacter(_, { characters, lastNarrative = {}, selectedAvatar, narrator }) {
    return getNarrativeExtraLeftCharacter(characters, lastNarrative, selectedAvatar, narrator);
  },
  lastNarrativeExtraRightCharacter(_, { characters, lastNarrative = {}, selectedAvatar, narrator }) {
    return getNarrativeExtraRightCharacter(characters, lastNarrative, selectedAvatar, narrator);
  },
  narrative({ narratives = [], narrativeIndex = -1 }, { lastNarrative = {} }) {
    if (narrativeIndex >= 0 && narratives[narrativeIndex]) {
      if (narrativeIndex === 0 || (lastNarrative && lastNarrative.type && lastNarrative.type.includes('Decision'))) {
        // can't go back to a decision narrative or beyond the start of the narratives
        // TODO: support going back beyond the start of the narratives
        narratives[narrativeIndex].forceEnableBack = false;
        narratives[narrativeIndex].preventEnableBack = true;
      }
      return narratives[narrativeIndex];
    }
    return undefined;
  },
  narrativeBackground(
    { narratives = [], narrativeIndex = -1, lastBackground = '' },
    { narrative = {}, playerRole = {} },
  ) {
    return getNarrativeBackground(narrative, playerRole, narratives, narrativeIndex, lastBackground);
  },
  narrativeLeftCharacter(_, { characters, narrative = {}, selectedAvatar, narrator }) {
    return getNarrativeLeftCharacter(characters, narrative, selectedAvatar, narrator);
  },
  narrativeRightCharacter(_, { characters, narrative = {}, selectedAvatar, narrator }) {
    return getNarrativeRightCharacter(characters, narrative, selectedAvatar, narrator);
  },
  narrativeExtraLeftCharacter(_, { characters, narrative = {}, selectedAvatar, narrator }) {
    return getNarrativeExtraLeftCharacter(characters, narrative, selectedAvatar, narrator);
  },
  narrativeExtraRightCharacter(_, { characters, narrative = {}, selectedAvatar, narrator }) {
    return getNarrativeExtraRightCharacter(characters, narrative, selectedAvatar, narrator);
  },
  leftVariant(_, { narrativeLeftCharacter, narrative = {} }) {
    if (narrative.leftVariant != null
      && narrativeLeftCharacter.imageVariants != null
      && narrativeLeftCharacter.imageVariants.length > 0) {
      return narrativeLeftCharacter.imageVariants.find(
        v => v.name === narrative.leftVariant);
    }
    return null;
  },
  lastLeftVariant(_, { lastNarrativeLeftCharacter, lastNarrative = {} }) {
    const lastLeftVariantName =
      (getNarrativeIsDialogWithResponse(lastNarrative) && lastNarrative.leftExtraVariant)
        ? lastNarrative.leftExtraVariant
        : lastNarrative.leftVariant;
    if (
      lastLeftVariantName
      && lastNarrativeLeftCharacter.imageVariants != null
      && lastNarrativeLeftCharacter.imageVariants.length > 0
    ) {
      return lastNarrativeLeftCharacter.imageVariants.find(
        v => v.name === lastLeftVariantName);
    }
    return null;
  },
  rightVariant(_, { narrativeRightCharacter, narrative = {} }) {
    if (narrative.rightVariant != null
      && narrativeRightCharacter.imageVariants != null
      && narrativeRightCharacter.imageVariants.length > 0) {
      return narrativeRightCharacter.imageVariants.find(
        v => v.name === narrative.rightVariant);
    }
    return null;
  },
  lastRightVariant(_, { lastNarrativeRightCharacter, lastNarrative = {} }) {
    const lastRightVariantName =
      (getNarrativeIsDialogWithResponse(lastNarrative) && lastNarrative.rightExtraVariant)
        ? lastNarrative.rightExtraVariant
        : lastNarrative.rightVariant;
    if (
      lastRightVariantName
      && lastNarrativeRightCharacter.imageVariants != null
      && lastNarrativeRightCharacter.imageVariants.length > 0
    ) {
      return lastNarrativeRightCharacter.imageVariants.find(
        v => v.name === lastRightVariantName);
    }
    return null;
  },
  extraLeftVariant(_, { narrativeExtraLeftCharacter, narrative = {} }) {
    if (narrative.extraLeftVariant != null
      && narrativeExtraLeftCharacter.imageVariants != null
      && narrativeExtraLeftCharacter.imageVariants.length > 0) {
      return narrativeExtraLeftCharacter.imageVariants.find(
        v => v.name === narrative.extraLeftVariant);
    }
    return null;
  },
  lastExtraLeftVariant(_, { lastNarrativeExtraLeftCharacter, lastNarrative = {} }) {
    const lastExtraLeftVariantName =
      (getNarrativeIsDialogWithResponse(lastNarrative) && lastNarrative.extraLeftExtraVariant)
        ? lastNarrative.extraLeftExtraVariant
        : lastNarrative.extraLeftVariant;
    if (
      lastExtraLeftVariantName
      && lastNarrativeExtraLeftCharacter.imageVariants != null
      && lastNarrativeExtraLeftCharacter.imageVariants.length > 0
    ) {
      return lastNarrativeExtraLeftCharacter.imageVariants.find(
        v => v.name === lastExtraLeftVariantName);
    }
    return null;
  },
  extraRightVariant(_, { narrativeExtraRightCharacter, narrative = {} }) {
    if (narrative.extraRightVariant != null
      && narrativeExtraRightCharacter.imageVariants != null
      && narrativeExtraRightCharacter.imageVariants.length > 0) {
      return narrativeExtraRightCharacter.imageVariants.find(
        v => v.name === narrative.extraRightVariant);
    }
    return null;
  },
  lastExtraRightVariant(_, { lastNarrativeExtraRightCharacter, lastNarrative = {} }) {
    const lastExtraRightVariantName =
      (getNarrativeIsDialogWithResponse(lastNarrative) && lastNarrative.extraRightExtraVariant)
        ? lastNarrative.extraRightExtraVariant
        : lastNarrative.extraRightVariant;
    if (
      lastExtraRightVariantName
      && lastNarrativeExtraRightCharacter.imageVariants != null
      && lastNarrativeExtraRightCharacter.imageVariants.length > 0
    ) {
      return lastNarrativeExtraRightCharacter.imageVariants.find(
        v => v.name === lastExtraRightVariantName);
    }
    return null;
  },
  nextNarrative({ narratives = [], narrativeIndex = -1 }) {
    const nextIndex = narrativeIndex + 1;
    return nextIndex < 0 ? undefined : narratives[nextIndex];
  },
  nextNarrativeBackground(
    { narratives = [], narrativeIndex = -1, lastBackground = '' },
    { nextNarrative = {}, playerRole = {} },
  ) {
    return getNarrativeBackground(nextNarrative, playerRole, narratives, narrativeIndex + 1, lastBackground);
  },
  nextNarrativeLeftCharacter(_, { characters, nextNarrative = {}, selectedAvatar }) {
    return getNarrativeLeftCharacter(characters, nextNarrative, selectedAvatar);
  },
  nextNarrativeRightCharacter(_, { characters, nextNarrative = {}, selectedAvatar }) {
    return getNarrativeRightCharacter(characters, nextNarrative, selectedAvatar);
  },
  nextNarrativeExtraLeftCharacter(_, { characters, nextNarrative = {}, selectedAvatar }) {
    return getNarrativeExtraLeftCharacter(characters, nextNarrative, selectedAvatar);
  },
  nextNarrativeExtraRightCharacter(_, { characters, nextNarrative = {}, selectedAvatar }) {
    return getNarrativeExtraRightCharacter(characters, nextNarrative, selectedAvatar);
  },
  leftCharacterAnimateEnter(_, {
    narrative,
    lastNarrative,
    lastNarrativeLeftCharacter,
    leftVariant,
    narrativeLeftCharacter
  }) {
    return (!lastNarrative ||
      lastNarrativeLeftCharacter._id !== narrativeLeftCharacter._id) &&
      (leftVariant == null || !(leftVariant.noAnimation || leftVariant.noAnimationEnter)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationEnter));
  },
  leftCharacterAnimateLeave(_, {
    narrative,
    nextNarrative,
    narrativeLeftCharacter,
    leftVariant,
    nextNarrativeLeftCharacter
  }) {
    return (!nextNarrative ||
      narrativeLeftCharacter._id !== nextNarrativeLeftCharacter._id) &&
      (leftVariant == null || !(leftVariant.noAnimation || leftVariant.noAnimationLeave)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationLeave));
  },
  leftCharacterVariantChangeWithBubble(_, {
    narrative,
    narrativeLeftCharacter,
    leftVariant,
    lastNarrativeLeftCharacter,
    lastLeftVariant,
    leftCharacterAnimateEnter,
  }) {
    return (!leftCharacterAnimateEnter && (narrative || {}).speaker === 'left'
      && lastNarrativeLeftCharacter && narrativeLeftCharacter
      && narrativeLeftCharacter._id === lastNarrativeLeftCharacter._id
      && (leftVariant || {}).name !== (lastLeftVariant || {}).name);
  },
  leftVariantBeforeBubble(_, {
    leftVariant,
    lastLeftVariant,
    leftCharacterVariantChangeWithBubble,
  }) {
    return leftCharacterVariantChangeWithBubble ? lastLeftVariant : leftVariant;
  },
  rightCharacterAnimateEnter(_, {
    narrative,
    lastNarrative,
    lastNarrativeRightCharacter,
    rightVariant,
    narrativeRightCharacter
  }) {
    return (!lastNarrative ||
      lastNarrativeRightCharacter._id !== narrativeRightCharacter._id) &&
      (rightVariant == null || !(rightVariant.noAnimation || rightVariant.noAnimationEnter)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationEnter));
  },
  rightCharacterAnimateLeave(_, {
    narrative,
    nextNarrative,
    narrativeRightCharacter,
    rightVariant,
    nextNarrativeRightCharacter
  }) {
    return (!nextNarrative ||
      narrativeRightCharacter._id !== nextNarrativeRightCharacter._id) &&
      (rightVariant == null || !(rightVariant.noAnimation || rightVariant.noAnimationLeave)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationLeave));
  },
  rightCharacterVariantChangeWithBubble(_, {
    narrative,
    narrativeRightCharacter,
    rightVariant,
    lastNarrativeRightCharacter,
    lastRightVariant,
    rightCharacterAnimateEnter,
  }) {
    return (!rightCharacterAnimateEnter && (narrative || {}).speaker === 'right'
      && lastNarrativeRightCharacter && narrativeRightCharacter
      && narrativeRightCharacter._id === lastNarrativeRightCharacter._id
      && (rightVariant || {}).name !== (lastRightVariant || {}).name);
  },
  rightVariantBeforeBubble(_, {
    rightVariant,
    lastRightVariant,
    rightCharacterVariantChangeWithBubble,
  }) {
    return rightCharacterVariantChangeWithBubble ? lastRightVariant : rightVariant;
  },
  extraLeftCharacterAnimateEnter(_, {
    narrative,
    lastNarrative,
    lastNarrativeExtraLeftCharacter,
    extraLeftVariant,
    narrativeExtraLeftCharacter
  }) {
    return (!lastNarrative ||
      lastNarrativeExtraLeftCharacter._id !== narrativeExtraLeftCharacter._id) &&
      (extraLeftVariant == null || !(extraLeftVariant.noAnimation || extraLeftVariant.noAnimationEnter)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationEnter));
  },
  extraLeftCharacterAnimateLeave(_, {
    narrative,
    nextNarrative,
    narrativeExtraLeftCharacter,
    extraLeftVariant,
    nextNarrativeExtraLeftCharacter
  }) {
    return (!nextNarrative ||
      narrativeExtraLeftCharacter._id !== nextNarrativeExtraLeftCharacter._id) &&
      (extraLeftVariant == null || !(extraLeftVariant.noAnimation || extraLeftVariant.noAnimationLeave)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationLeave));
  },
  extraLeftCharacterVariantChangeWithBubble(_, {
    narrative,
    narrativeExtraLeftCharacter,
    extraLeftVariant,
    lastNarrativeExtraLeftCharacter,
    lastExtraLeftVariant,
    extraLeftCharacterAnimateEnter,
  }) {
    return (!extraLeftCharacterAnimateEnter && (narrative || {}).speaker === 'extra-left'
      && lastNarrativeExtraLeftCharacter && narrativeExtraLeftCharacter
      && narrativeExtraLeftCharacter._id === lastNarrativeExtraLeftCharacter._id
      && (extraLeftVariant || {}).name !== (lastExtraLeftVariant || {}).name);
  },
  extraLeftVariantBeforeBubble(_, {
    extraLeftVariant,
    lastExtraLeftVariant,
    extraLeftCharacterVariantChangeWithBubble,
  }) {
    return extraLeftCharacterVariantChangeWithBubble ? lastExtraLeftVariant : extraLeftVariant;
  },
  extraRightCharacterAnimateEnter(_, {
    narrative,
    lastNarrative,
    lastNarrativeExtraRightCharacter,
    extraRightVariant,
    narrativeExtraRightCharacter
  }) {
    return (!lastNarrative ||
      lastNarrativeExtraRightCharacter._id !== narrativeExtraRightCharacter._id) &&
      (extraRightVariant == null || !(extraRightVariant.noAnimation || extraRightVariant.noAnimationEnter)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationEnter));
  },
  extraRightCharacterAnimateLeave(_, {
    narrative,
    nextNarrative,
    narrativeExtraRightCharacter,
    extraRightVariant,
    nextNarrativeExtraRightCharacter
  }) {
    return (!nextNarrative ||
      narrativeExtraRightCharacter._id !== nextNarrativeExtraRightCharacter._id) &&
      (extraRightVariant == null || !(extraRightVariant.noAnimation || extraRightVariant.noAnimationLeave)) &&
      (narrative == null || !(narrative.noAnimation || narrative.noAnimationLeave));
  },
  extraRightCharacterVariantChangeWithBubble(_, {
    narrative,
    narrativeExtraRightCharacter,
    extraRightVariant,
    lastNarrativeExtraRightCharacter,
    lastExtraRightVariant,
    extraRightCharacterAnimateEnter,
  }) {
    return (!extraRightCharacterAnimateEnter && (narrative || {}).speaker === 'extra-right'
      && lastNarrativeExtraRightCharacter && narrativeExtraRightCharacter
      && narrativeExtraRightCharacter._id === lastNarrativeExtraRightCharacter._id
      && (extraRightVariant || {}).name !== (lastExtraRightVariant || {}).name);
  },
  extraRightVariantBeforeBubble(_, {
    extraRightVariant,
    lastExtraRightVariant,
    extraRightCharacterVariantChangeWithBubble,
  }) {
    return extraRightCharacterVariantChangeWithBubble ? lastExtraRightVariant : extraRightVariant;
  },
  narrativeCurrentChallenge(_, { challenge, lastChallenge, lastChallengeOutroViewed }) {
    const displayLastChallenge = lastChallenge && !lastChallengeOutroViewed;
    return displayLastChallenge ? lastChallenge : challenge;
  },
  narrativePlayerName(_, { me = {} }) {
    return me.firstName || me.email || '';
  },
  narrativeLeftSpeaker(_, {
    characters,
    narrative: { leftPlayer, leftCharacter } = {},
    narrativePlayerName
  }) {
    if (leftPlayer) return narrativePlayerName;
    const { title } = characters[leftCharacter] || {};
    return title || '';
  },
  narrativeRightSpeaker(_, {
    characters,
    narrative: { rightPlayer, rightCharacter } = {},
    narrativePlayerName
  }) {
    if (rightPlayer) return narrativePlayerName;
    const { title } = characters[rightCharacter] || {};
    return title || '';
  },
  narrativeCenterSpeaker(_, {
    characters,
    narrative: { centerPlayer, centerCharacter } = {},
    narrativePlayerName
  }) {
    if (centerPlayer) return narrativePlayerName;
    const { title } = characters[centerCharacter] || {};
    return title || '';
  },
  narrativeNavbarText(_, {
    narrative: { navbarText = '' } = {},
    interpolateRegex,
    replacements
  }) {
    return navbarText.replace(interpolateRegex, (__, tag) => replacements[tag] || '');
  },
  narrativeText(_, {
    narrative: { text = '' } = {},
    interpolateRegex,
    replacements
  }) {
    return text.replace(interpolateRegex, (__, tag) => replacements[tag] || '');
  },
  narrativeOptionalText(_, {
    narrative: { optionalText = '' } = {},
    interpolateRegex,
    replacements
  }) {
    return optionalText.replace(interpolateRegex, (__, tag) => replacements[tag] || '');
  },
};

export default {
  state,
  mutations,
  actions,
  getters
};



// WEBPACK FOOTER //
// ./src/store/game/narrative.js